import type { IconType } from "react-icons";
import { FaBalanceScale, FaClipboardCheck, FaClipboardList, FaFileAlt, FaGavel, FaInfoCircle, FaTags, FaUserCheck,} from "react-icons/fa";
import { useTranslation } from "react-i18next";

export const WIZARD_STEPS = [
  { key: "info", icon: FaInfoCircle },
  { key: "classification", icon: FaTags },
  { key: "procedure", icon: FaGavel },
  { key: "qualification", icon: FaUserCheck },
  { key: "awardCriteria", icon: FaBalanceScale },
  { key: "minimumRequirements", icon: FaClipboardCheck },
  { key: "documents", icon: FaFileAlt },
  { key: "summary", icon: FaClipboardList },
] as const satisfies ReadonlyArray<{ key: string; icon: IconType }>;

export type PublicEventStep = (typeof WIZARD_STEPS)[number]["key"];

export const WIZARD_STEP_KEYS = WIZARD_STEPS.map((s) => s.key) as readonly PublicEventStep[];

export const useStepLabel = (): ((key: PublicEventStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "info":
        return t("Basic information");
      case "classification":
        return t("Classification and contract");
      case "procedure":
        return t("Procedure");
      case "qualification":
        return t("Qualification requirements");
      case "awardCriteria":
        return t("Award criteria");
      case "minimumRequirements":
        return t("Minimum requirements");
      case "documents":
        return t("Documents");
      case "summary":
        return t("Summary");
    }
  };
};

/** Short labels for the horizontal process bar (full labels are kept for buttons + the mobile drawer). */
export const useStepShortLabel = (): ((key: PublicEventStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "info":
        return t("Basics");
      case "classification":
        return t("Classification");
      case "procedure":
        return t("Procedure");
      case "qualification":
        return t("Qualification");
      case "awardCriteria":
        return t("Award criteria");
      case "minimumRequirements":
        return t("Minimum requirements");
      case "documents":
        return t("Documents");
      case "summary":
        return t("Summary");
    }
  };
};

export const stepNeighbors = (
  current: PublicEventStep,
): { previous: PublicEventStep | undefined; next: PublicEventStep | undefined } => {
  const idx = WIZARD_STEP_KEYS.indexOf(current);
  return {
    previous: idx > 0 ? WIZARD_STEP_KEYS[idx - 1] : undefined,
    next: idx < WIZARD_STEP_KEYS.length - 1 ? WIZARD_STEP_KEYS[idx + 1] : undefined,
  };
};
